import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'

import { useSWRConfig } from 'swr'

import { fetchRest } from '@/common/api/fetcher'
import { setUnauthorizedHandler } from '@/common/api/session'
import { ChildProps } from '@/common/util/props'
import { HttpStatusCode } from '@/common/util/status'
import { LocalStorage, loadStorage, removeStorage, saveStorage } from '@/common/util/storage'

/** ログイン中の運用者。/api/auth/me と /api/auth/login が返す形 */
export interface AuthOperator {
  id: number
  name?: string | null
  email?: string | null
  is_admin: boolean
  /** 管理者は null。サイト運用者は所属サイト */
  site_id: number | null
  [key: string]: unknown
}

/** 画面モード。管理者はサイトを選ぶまで null */
export type AuthMode = 'admin' | 'site' | null

export interface AuthValue {
  token: string | null
  operator: AuthOperator | null
  mode: AuthMode
  selectedSiteId: number | null
  loading: boolean
  login(loginId: string, password: string): Promise<AuthOperator>
  logout(): Promise<void>
  setMode(newMode: AuthMode, siteId?: number | null): void
  /** fetch に渡す共通ヘッダ（Content-Type と Bearer トークン） */
  authHeaders(): Record<string, string>
}

// 既定値を置かない。Provider の外で使ったら例外にしたいので null のままにし、
// useAuth 側で潰す（各画面が毎回 null 判定を書かずに済む）
const AuthContext = createContext<AuthValue | null>(null)

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    // Provider の外から呼ばれている＝組み立ての誤り。黙って undefined を配ると
    // 画面側が「ログインしていない」と誤解した挙動になるので、ここで止める
    throw new Error('useAuth は AuthProvider の内側で使ってください')
  }
  return ctx
}

export function AuthProvider({ children }: ChildProps) {
  const [token, setToken] = useState<string | null>(() => loadStorage(LocalStorage.AUTH_TOKEN))
  const [operator, setOperator] = useState<AuthOperator | null>(null)
  const [mode, setModeState] = useState<AuthMode>(() => localStorage.getItem('w2_mode') as AuthMode)
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(() => {
    const v = localStorage.getItem('w2_selected_site')
    return v ? Number(v) : null
  })
  const [loading, setLoading] = useState(true)
  const { mutate } = useSWRConfig()

  const authHeaders = (t?: string | null): Record<string, string> => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${t || loadStorage(LocalStorage.AUTH_TOKEN) || ''}`
  })

  /**
   * セッション切れ時の削除。state・localStorage・**SWR キャッシュ**を空にする。
   */
  const handleSessionExpired = useCallback(() => {
    // 401 は並行リクエストの数だけ返る。片付け済みなら何もしない
    // （トークンと mode/site は常に同時に読み書きしているので、これで足りる）
    if (!loadStorage(LocalStorage.AUTH_TOKEN)) return
    removeStorage(LocalStorage.AUTH_TOKEN)
    localStorage.removeItem('w2_mode')
    localStorage.removeItem('w2_selected_site')
    setToken(null)
    setOperator(null)
    setModeState(null)
    setSelectedSiteId(null)
    mutate(() => true, undefined, { revalidate: false })
  }, [mutate])

  // 認証付きリクエストが 401 を返したときの後始末を登録する。
  // 検知するのは `fetchRest`（＝SWR と直接呼び出しの両方が通る中心）。
  useEffect(() => {
    setUnauthorizedHandler(handleSessionExpired)
    return () => setUnauthorizedHandler(null)
  }, [handleSessionExpired])

  // TODO: Consider removing this legacy fetch override once all pages have migrated to fetchRest
  // **旧 `pages/*.jsx` のための保険。** 置き換え待ちの画面がまだ素の fetch +
  // authHeaders() で叩いており、`fetchRest` を通らない。それらの 401 を拾うために
  // `window.fetch` を差し替えているが、以前のように全 fetch を対象にすると
  // 外部 API・Sentry の送信の 401 まで巻き込むため、条件を次まで絞っている:
  //   - 同一オリジンの `/api/` 配下だけ
  //   - `/api/auth/login`（401＝パスワード誤り）と `/api/auth/me`（復元処理が自分で扱う）は除外
  //   - トークンを持っているときだけ（未ログイン中の 401 に反応しない）
  // **jsx が無くなったらこの useEffect ごと消すこと。**
  useEffect(() => {
    const originalFetch = window.fetch
    const EXCLUDED = ['/api/auth/login', '/api/auth/me']

    window.fetch = async (...args: Parameters<typeof fetch>) => {
      const response = await originalFetch(...args)
      if (response.status !== HttpStatusCode.UNAUTHORIZED) return response
      if (!loadStorage(LocalStorage.AUTH_TOKEN)) return response

      const input = args[0]
      const raw =
        typeof input === 'string' ? input : input instanceof URL ? input.href : input?.url || ''
      try {
        const { origin, pathname } = new URL(raw, window.location.origin)
        if (origin === window.location.origin && pathname.startsWith('/api/')) {
          if (!EXCLUDED.includes(pathname)) handleSessionExpired()
        }
      } catch {
        // URL として解釈できないものは対象外。ここで投げると呼び出し元の fetch が壊れる
      }
      return response
    }
    return () => {
      window.fetch = originalFetch
    }
  }, [handleSessionExpired])

  // Restore session on mount
  useEffect(() => {
    const storedToken = loadStorage(LocalStorage.AUTH_TOKEN)
    if (!storedToken) {
      setLoading(false)
      return
    }
    fetch('/api/auth/me', { headers: authHeaders(storedToken) })
      .then(async (r) => {
        if (r.status === HttpStatusCode.UNAUTHORIZED) {
          handleSessionExpired()
          return
        }
        // 5xx 等は「認証できたかどうか判断できない」状態。トークンは残す
        if (!r.ok) return
        const op: AuthOperator = await r.json()
        setOperator(op)
        // Non-admin: force site mode
        if (!op.is_admin) {
          setModeState('site')
          setSelectedSiteId(op.site_id)
          localStorage.setItem('w2_mode', 'site')
          localStorage.setItem('w2_selected_site', String(op.site_id))
        }
      })
      .catch(() => {
        // 通信断・タイムアウト・リロードによる中断。認証の可否は判断できないので
        // トークンは残す（消すとリロードのたびにログアウトする）
      })
      .finally(() => setLoading(false))
  }, [])

  const login = async (login_id: string, password: string): Promise<AuthOperator> => {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ login_id, password })
    })
    if (!res.ok) {
      let detail = 'Login failed'
      try {
        const err = await res.json()
        detail = err.detail || detail
      } catch {
        // レスポンスがJSONでないときは既定の文言のまま
      }
      throw new Error(detail)
    }
    const data = await res.json()
    setToken(data.token)
    setOperator(data.operator)
    saveStorage(LocalStorage.AUTH_TOKEN, data.token)

    if (!data.operator.is_admin) {
      setModeState('site')
      setSelectedSiteId(data.operator.site_id)
      localStorage.setItem('w2_mode', 'site')
      localStorage.setItem('w2_selected_site', String(data.operator.site_id))
    } else {
      // Admin: clear mode so ModeSelect shows
      setModeState(null)
      localStorage.removeItem('w2_mode')
      localStorage.removeItem('w2_selected_site')
    }
    return data.operator
  }

  const logout = async (): Promise<void> => {
    if (token) {
      await fetchRest('/api/auth/logout', { method: 'POST' }).catch(() => {})
    }
    setToken(null)
    setOperator(null)
    setModeState(null)
    setSelectedSiteId(null)
    removeStorage(LocalStorage.AUTH_TOKEN)
    localStorage.removeItem('w2_mode')
    localStorage.removeItem('w2_selected_site')
    mutate(() => true, undefined, { revalidate: false })
  }

  const setMode = (newMode: AuthMode, siteId?: number | null): void => {
    setModeState(newMode)
    if (newMode) localStorage.setItem('w2_mode', newMode)
    else localStorage.removeItem('w2_mode')
    if (siteId !== undefined) {
      setSelectedSiteId(siteId)
      if (siteId !== null) {
        localStorage.setItem('w2_selected_site', String(siteId))
      } else {
        localStorage.removeItem('w2_selected_site')
      }
    }
  }

  const value: AuthValue = {
    token,
    operator,
    mode,
    selectedSiteId,
    loading,
    login,
    logout,
    setMode,
    authHeaders: () => authHeaders()
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
